import { Injectable, Injector, NgZone } from '@angular/core';
import { Router } from '@angular/router';
import { TranslocoService } from '@jsverse/transloco';
import { Observable, forkJoin, of } from 'rxjs';
import { catchError, map, shareReplay, tap, timeout } from 'rxjs/operators';
import { StoreEvent, StoreRealtimeService } from '../../../core/services/store-realtime.service';
import { DeviceIdentityService } from '../../../core/services/device-identity.service';
import { ToastService } from '../../../core/services/toast.service';
import { ApiService } from '../../../core/services/api.service';
import { LockAwareAlerts, within } from './lock-aware-alerts';
import { isPosOperatorScreen } from './pos-operator-screen';

/** Longer than a confirmation: a kitchen that never got its ticket is not
 *  cooking, and somebody has to walk over or reprint. */
export const PRINT_FAILED_TOAST_MS = 12000;

/** How many alerts a locked terminal keeps for its next unlock; the oldest go first. */
export const PRINT_FAILED_HELD_MAX = 5;

/** The cloud's event for a kitchen whose chit or labels were not produced (KC-0 / LBL-1). */
export const KITCHEN_SEND_FAILED = 'kitchen.send.failed';

/** How long a `kitchen.send.failed` toast waits for each name it reads; then it goes without it. */
export const KITCHEN_SEND_FAILED_LOOKUP_MS = 4000;

/** How long a store's kitchen names are reused before they are read again. */
export const KITCHEN_NAMES_TTL_MS = 10 * 60 * 1000;

/**
 * How long one read of a check serves every `kitchen.send.failed` for that
 * check. They come in bursts — one event per failed kitchen, on every
 * terminal — and each publish evicts the cloud's cached check, so each read
 * would reach the database.
 */
export const KITCHEN_SEND_FAILED_CHECK_SHARE_MS = 5000;

/**
 * A held `kitchen.send.failed` alert this old is dropped instead of raised
 * at the unlock. It goes to every terminal, so a Waiter terminal locked for
 * a while collected other waiters' tables, and ten minutes on the kitchen
 * has the round on its KDS or someone has reprinted.
 */
export const KITCHEN_SEND_FAILED_HELD_MAX_AGE_MS = 10 * 60 * 1000;

/**
 * Any other held alert (a hub print.failed, this terminal's own bill print)
 * is kept longer, since a hub KOT that printed nowhere may be the only word
 * a kitchen without a KDS gets, but not past a shift: a terminal locked at
 * closing must not greet the next morning with last night's "Bill for T4 …
 * not printed". The end of the POS session and a change of store clear the
 * holds outright (see LockAwareAlerts' `onPosAuth`); this cap covers a
 * terminal that was only locked.
 */
export const PRINT_FAILED_HELD_MAX_AGE_MS = 12 * 60 * 60 * 1000;

/**
 * OFP-093 — tell the cashier when the offline hub printed a document nowhere.
 *
 * Every auto-print on the hub is fire-and-forget: a KOT or void-KOT (one
 * frame per kitchen), the receipt after a payment, the bill after Bill, and
 * the cash-sale drawer kick. When printers were routed for one and none took
 * it (or it could not be built, or every printer mapped to its kitchen is
 * inactive) the hub broadcasts a `print.failed` frame on its LAN realtime
 * socket. Before this the failure reached nobody who could act on it: the
 * hub's Print Monitor is an in-memory list on the hub, and a kitchen whose
 * chit never printed simply did not cook.
 *
 * ROOT-level, started once from AppComponent — not in the POS shell, which
 * is mounted only on `/pos/terminal`. A KOT fails while the order screen is
 * still up (a fast failure, or "Send & stay") and a receipt while the settle
 * screen is; both are sibling routes of the shell (SO-2's
 * ScheduledOrderAlertsService is here for the same reason).
 *
 * A LOCKED terminal holds its alerts (the newest PRINT_FAILED_HELD_MAX, one
 * per document; a kitchen.send.failed one for KITCHEN_SEND_FAILED_HELD_MAX_AGE_MS
 * at most, any other for PRINT_FAILED_HELD_MAX_AGE_MS, none past the end of
 * the POS session) and raises them at the unlock; so does an alert still on
 * screen when the lock comes down, unless the operator had tapped it away
 * (LockAwareAlerts, shared with TCPAY-1's payment alerts). Waiter and Hybrid terminals lock right
 * after Send, a payment and a Bill, and on idle — exactly when these frames
 * arrive — and a toast sits under the lock overlay (z-index 9999 against
 * --z-lock 10100) until it expires, unseen, on the one terminal a receipt,
 * bill or drawer frame names. The overlay's z-order is left alone: nothing
 * else on the terminal may show above it.
 *
 * Frame (edge `LocalPrintJobCreationService._announceNotPrinted`; edge
 * docs/EDGE-OFFLINE-PARITY-RELEASE-NOTES.md): `status` is the document kind
 * (`Kot`, `VoidKot`, `Receipt`, `Bill`, `XReport`, `OpenDrawer`) and
 * `summary` a JSON string `{documentType, reason, deviceId?, offline,
 * kitchenName?, orderNo?, tableName?, checkNumber?}`.
 *
 * The CLOUD's counterpart, `kitchen.send.failed` (KotPrintJobCreationService,
 * on AppSync), goes through the same rules: a kitchen's chit was not produced
 * because every printer mapped to it is inactive (`NoActivePrinter`), or its
 * cup labels were not, for want of a label template (`NoLabelTemplate`) or
 * over the per-order cap (`LabelCapExceeded`). It names no terminal, so like
 * a KOT it goes to every one.
 *
 * What arrives depends on the cloud's version. A cloud older than PR #454
 * serializes a fixed field list that drops `orderId`, `kitchenId` and
 * `summary`, so its events carry only `{type, tenantId, storeId, checkId,
 * status, changedAt}`: one identical event per failed kitchen. From #454 on
 * they carry `orderId` and `kitchenId` too (never `summary`). The toast leads
 * with what the check says (its table, the round or the check's number), read
 * once per burst, and names the kitchen when its name can be read. One toast
 * per thing it names: one per round without ids, or without a readable kitchen
 * name (a Waiter is refused the kitchen list); one per kitchen with them
 * (see `kitchenSendFailedAlertKey` and `raise`).
 */
@Injectable({ providedIn: 'root' })
export class PrintFailedAlertsService {
  private started = false;

  /**
   * Kitchen names by id (lower case), per store and per the signed-in
   * user's roles (the list is refused by role), read at most once per
   * KITCHEN_NAMES_TTL_MS.
   */
  private kitchenNames = new Map<string, { at: number; names$: Observable<Map<string, string> | null> }>();

  /** Recent check reads by check id, shared for KITCHEN_SEND_FAILED_CHECK_SHARE_MS. */
  private checkReads = new Map<string, { at: number; check$: Observable<unknown> }>();

  /** The toasts: raised now, or held while the terminal is locked and raised at the unlock. */
  private readonly alerts: LockAwareAlerts;

  constructor(
    private readonly realtime: StoreRealtimeService,
    private readonly deviceIdentity: DeviceIdentityService,
    toast: ToastService,
    private readonly transloco: TranslocoService,
    private readonly router: Router,
    private readonly zone: NgZone,
    // PosStateService is resolved from here when a frame first needs it, not
    // injected: AppComponent starts this service on every surface (KDS,
    // kiosk, displays), and constructing PosStateService runs the POS boot —
    // the stored PIN session, its language, its lock — on all of them.
    injector: Injector,
    // kitchen.send.failed — reads the check and the kitchen names its toast
    // leads with. Optional and LAST so positional spec constructions keep
    // compiling; DI always supplies it (providedIn: 'root'). Without it the
    // toast goes out without names.
    private readonly api?: ApiService,
  ) {
    this.alerts = new LockAwareAlerts(toast, zone, injector, PRINT_FAILED_HELD_MAX, '[print.failed]');
  }

  start(): void {
    if (this.started) return;
    this.started = true;
    this.realtime.event$.subscribe(e => this.zone.run(() => {
      try {
        this.handle(e);
      } catch (err) {
        // A toast that cannot be built must not end the subscription and
        // with it every later alert.
        // eslint-disable-next-line no-console
        console.error('[print.failed] alert threw — subscription kept', err);
      }
    }));
  }

  /** Public for tests. */
  handle(e: StoreEvent | null | undefined): void {
    if (e?.type !== 'print.failed' && e?.type !== KITCHEN_SEND_FAILED) return;
    // Cashier screens only: the KDS, kiosk and the displays share this
    // socket, and the PIN pad has nobody signed in to act on it.
    if (!isPosOperatorScreen(this.router.url)) return;
    const summary = parsePrintFailedSummary(e.summary);
    if (!printFailedIsForThisDevice(summary, this.deviceIdentity.getEdgeClientId())) return;
    if (e.type === KITCHEN_SEND_FAILED) {
      this.kitchenSendFailed(e, summary);
      return;
    }
    const message = printFailedToastText(e, summary, (key, params) => this.transloco.translate(key, params));
    this.deliver(printFailedAlertKey(e, summary), message);
  }

  /**
   * A print failure this terminal learned from a REPLY rather than a hub
   * frame: the order screen's post-Bill print that failed for a reason other
   * than "no printer". Same rules as a frame: shown at once on an unlocked
   * terminal, held while it is locked and raised at the unlock, held again
   * if a lock comes down over it. A Waiter / Hybrid terminal locks right
   * after Bill, which destroyed the screen's own 2.5 s toast unread.
   * `alertKey` names the document as printFailedAlertKey does
   * (`bill|<checkId>|`), so a repeat replaces it while held. The message is
   * translated now, as a frame's is.
   */
  raiseLocal(alertKey: string, messageKey: string, params?: Record<string, unknown>): void {
    this.deliver(alertKey, this.transloco.translate(messageKey, params));
  }

  /** Raise an alert now, or hold it for the unlock while the terminal is locked. */
  private deliver(key: string, message: string, maxAgeMs = PRINT_FAILED_HELD_MAX_AGE_MS): void {
    this.alerts.deliver(key, message, PRINT_FAILED_TOAST_MS, maxAgeMs);
  }

  /**
   * The cloud's `kitchen.send.failed`. The event names ids only, so the
   * table and the check (and the order round, given an `orderId`) are read
   * off the check: GET pos/checks/{id}, which every POS role may read, one
   * read per check per KITCHEN_SEND_FAILED_CHECK_SHARE_MS. Given a
   * `kitchenId`, the kitchen's name is read off the store's kitchens (GET
   * stores/{id}/printing/kitchens, kept for KITCHEN_NAMES_TTL_MS; a Waiter is
   * refused it and gets no name). A cloud older than PR #454 sends neither
   * id (see the class comment); then only the check is read. Each read has
   * KITCHEN_SEND_FAILED_LOOKUP_MS: one that fails or is slow leaves its part
   * out, never the toast. A `summary` with the names, should the cloud ever
   * send one, is used instead of reading.
   */
  private kitchenSendFailed(e: StoreEvent, summary: PrintFailedSummary | null): void {
    // A status with no sentence of its own is left alone, not guessed at:
    // the plans add more (an expiry, a recovered dispatch), and a generic
    // "did not print" would be wrong for some of them.
    const reason = kitchenSendFailedReason(e.status);
    if (!reason) return;
    this.kitchenSendFailedContext(e, summary).subscribe(ctx => this.zone.run(() => {
      try {
        const message = kitchenSendFailedToastText(reason, ctx, (k, p) => this.transloco.translate(k, p));
        // Keyed by what the toast names, known only now: a kitchen whose
        // name could not be read (a Waiter) is no separate alert, or three
        // kitchens of one round would stack three identical toasts.
        this.deliver(kitchenSendFailedAlertKey(e, ctx), message, KITCHEN_SEND_FAILED_HELD_MAX_AGE_MS);
      } catch (err) {
        // eslint-disable-next-line no-console
        console.error('[kitchen.send.failed] alert could not be raised', err);
      }
    }));
  }

  private kitchenSendFailedContext(e: StoreEvent, summary: PrintFailedSummary | null): Observable<KitchenSendFailedContext> {
    const known: KitchenSendFailedContext = {
      kitchenName: summary?.kitchenName ?? null,
      tableName: summary?.tableName ?? null,
      orderNo: summary?.orderNo ?? null,
      checkNumber: summary?.checkNumber ?? null,
    };
    const api = this.api;
    const checkId = text(e.checkId);
    const storeId = text(e.storeId);
    const kitchenId = text(e.kitchenId);
    const round$ = api && checkId && !known.tableName && !known.orderNo && !known.checkNumber
      ? this.readRound(api, checkId, text(e.orderId))
      : of(null);
    const kitchen$ = api && storeId && kitchenId && !known.kitchenName
      ? this.readKitchenName(api, storeId, kitchenId)
      : of(null);
    return forkJoin({ round: round$, kitchen: kitchen$ }).pipe(map(({ round, kitchen }) => ({
      kitchenName: known.kitchenName ?? kitchen,
      tableName: known.tableName ?? round?.tableName ?? null,
      orderNo: known.orderNo ?? round?.orderNo ?? null,
      checkNumber: known.checkNumber ?? round?.checkNumber ?? null,
    })));
  }

  /** The table and the order round, off the check. Null when it cannot be read in time. */
  private readRound(api: ApiService, checkId: string, orderId: string | null): Observable<KitchenSendFailedRound | null> {
    return this.readCheck(api, checkId).pipe(map(check => kitchenSendFailedRound(check, orderId)));
  }

  /**
   * The check's data (null when it cannot be read in time), one read shared
   * by every event for that check within KITCHEN_SEND_FAILED_CHECK_SHARE_MS,
   * in flight or answered, whatever the answer.
   */
  private readCheck(api: ApiService, checkId: string): Observable<unknown> {
    const now = Date.now();
    for (const [id, read] of this.checkReads) {
      if (!within(read.at, now, KITCHEN_SEND_FAILED_CHECK_SHARE_MS)) this.checkReads.delete(id);
    }
    let read = this.checkReads.get(checkId);
    if (!read) {
      read = {
        at: now,
        check$: api.get<unknown>(`pos/checks/${checkId}`).pipe(
          map(res => (res?.success ? res.data ?? null : null)),
          timeout({ first: KITCHEN_SEND_FAILED_LOOKUP_MS, with: () => of(null) }),
          catchError(() => of(null)),
          shareReplay(1),
        ),
      };
      this.checkReads.set(checkId, read);
    }
    return read.check$;
  }

  /**
   * The kitchen's name, off the store's kitchens. The answer is kept for
   * KITCHEN_NAMES_TTL_MS per store and per the signed-in user's roles: the
   * list is refused by role (a Waiter every time), so a Waiter's refusal
   * must not leave the Manager who unlocks next without names, nor a
   * Manager's list serve a Waiter. A 403/404 refusal is kept; no answer, a
   * timeout, a 401, 408, 429 or a 5xx is tried again next event.
   */
  private readKitchenName(api: ApiService, storeId: string, kitchenId: string): Observable<string | null> {
    const now = Date.now();
    const cacheKey = `${storeId}|${this.posRoles()}`;
    let entry = this.kitchenNames.get(cacheKey);
    if (!entry || !within(entry.at, now, KITCHEN_NAMES_TTL_MS)) {
      const fresh: { at: number; names$: Observable<Map<string, string> | null> } = { at: now, names$: of(null) };
      const noAnswer: KitchenListAnswer = { names: null, keep: false };
      fresh.names$ = api.get<unknown>(`stores/${storeId}/printing/kitchens`).pipe(
        map(res => kitchenListAnswer(res)),
        timeout({ first: KITCHEN_SEND_FAILED_LOOKUP_MS, with: () => of(noAnswer) }),
        catchError(() => of(noAnswer)),
        tap(answer => { if (!answer.keep && this.kitchenNames.get(cacheKey) === fresh) this.kitchenNames.delete(cacheKey); }),
        map(answer => answer.names),
        shareReplay(1),
      );
      this.kitchenNames.set(cacheKey, fresh);
      entry = fresh;
    }
    return entry.names$.pipe(map(names => names?.get(kitchenId.toLowerCase()) ?? null));
  }

  /** The signed-in POS user's roles, in a stable order ('' when none is known). */
  private posRoles(): string {
    const roles = this.alerts.lockState()?.posAuth?.roles ?? [];
    return roles.map(r => String(r).toLowerCase()).sort().join(',');
  }
}

/** The `summary` fields a toast uses, each a trimmed non-empty string or null. */
export interface PrintFailedSummary {
  documentType: string | null;
  reason: string | null;
  deviceId: string | null;
  kitchenName: string | null;
  orderNo: string | null;
  tableName: string | null;
  checkNumber: string | null;
}

/** Document kinds with a sentence of their own; anything else is `other`. */
export type PrintFailedDocument = 'kot' | 'voidKot' | 'receipt' | 'bill' | 'xReport' | 'openDrawer' | 'other';

export type PrintFailedTranslate = (key: string, params?: Record<string, unknown>) => string;

/**
 * The frame's `summary`, or null when it is missing or is not a JSON object.
 * Never throws: a malformed summary still raises its toast, from `status`.
 */
export function parsePrintFailedSummary(summary: unknown): PrintFailedSummary | null {
  let raw: unknown = summary;
  if (typeof summary === 'string') {
    try {
      raw = JSON.parse(summary);
    } catch {
      return null;
    }
  }
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return null;
  const o = raw as Record<string, unknown>;
  return {
    documentType: text(o['documentType']),
    reason: text(o['reason']),
    deviceId: text(o['deviceId']),
    kitchenName: text(o['kitchenName']),
    orderNo: label(o['orderNo']),
    tableName: tableLabel(o['tableName']),
    checkNumber: checkNumberLabel(o['checkNumber']),
  };
}

/** A trimmed non-empty string; anything else is absent. */
function text(v: unknown): string | null {
  if (typeof v !== 'string') return null;
  const t = v.trim();
  return t.length > 0 ? t : null;
}

/** As `text`, but a number is a label too (`checkNumber` is an int). */
function label(v: unknown): string | null {
  if (typeof v === 'number') return Number.isFinite(v) ? String(v) : null;
  return text(v);
}

/** A table name, except the "—" the cloud's check read gives a check with no table ("Table —" names none). */
function tableLabel(v: unknown): string | null {
  const t = label(v);
  return t !== null && Array.from(t).every(isSpaceOrDash) ? null : t;
}

/** A space, a hyphen, or a dash from U+2012 to U+2015 (figure, en and em dash, horizontal bar). */
function isSpaceOrDash(ch: string): boolean {
  const code = ch.charCodeAt(0);
  return /\s/.test(ch) || ch === '-' || (code >= 0x2012 && code <= 0x2015);
}

/** The hub's receipt payload defaults a missing check number to 0, so 0 or
 *  less is no number at all ("Check #0" would name no check). */
function checkNumberLabel(v: unknown): string | null {
  const t = label(v);
  if (t === null) return null;
  const n = Number(t);
  return Number.isFinite(n) && n <= 0 ? null : t;
}

/**
 * Whether THIS terminal shows the frame. A `deviceId` names the terminal that
 * asked for the document (the hub reads it from the request's
 * X-Edge-Client-Device-Id), so only that one shows it. Without one — a KOT or
 * void-KOT, whose save does not carry the terminal down to the kitchen print,
 * or a summary that could not be read — every terminal shows it: a failure
 * told twice beats one told to nobody.
 */
export function printFailedIsForThisDevice(
  summary: PrintFailedSummary | null,
  thisDeviceId: string | null | undefined,
): boolean {
  const target = summary?.deviceId;
  if (!target) return true;
  const mine = (thisDeviceId ?? '').trim();
  return mine.length > 0 && mine.toLowerCase() === target.toLowerCase();
}

/**
 * Case- and separator-insensitive, so `VoidKot`, `VOID-KOT` and `void_kot`
 * agree. Takes `unknown`: `status` comes off the wire, and a number there
 * must read as `other`, not throw.
 */
export function printFailedDocument(documentType: unknown): PrintFailedDocument {
  const raw = typeof documentType === 'string' ? documentType : '';
  switch (raw.toLowerCase().replace(/[^a-z]/g, '')) {
    case 'kot': return 'kot';
    case 'voidkot': return 'voidKot';
    case 'receipt': return 'receipt';
    case 'bill': return 'bill';
    case 'xreport': return 'xReport';
    case 'opendrawer': return 'openDrawer';
    default: return 'other';
  }
}

/**
 * The toast: the hub's `reason` when it sent one (a sentence written for the
 * cashier, naming the printer that failed), otherwise a translated sentence
 * for the document kind; led by whatever names the order — table, order
 * number, check number, kitchen.
 */
export function printFailedToastText(
  e: { status?: unknown },
  summary: PrintFailedSummary | null,
  translate: PrintFailedTranslate,
): string {
  const doc = printFailedDocument(summary?.documentType ?? e.status);
  const message = summary?.reason ?? translate(`pos.printing.failed.${doc}`);
  const context: string[] = [];
  if (summary?.tableName) context.push(translate('pos.printing.failed.table', { name: summary.tableName }));
  if (summary?.orderNo) context.push(translate('pos.printing.failed.order', { no: summary.orderNo }));
  // A bare number reads "Check #42"; one that names itself (the cloud's
  // CHKSL1 "CHK-00000042") is shown as it is, not "Check #CHK-…".
  if (summary?.checkNumber) {
    context.push(/^\d+$/.test(summary.checkNumber)
      ? translate('pos.printing.failed.check', { no: summary.checkNumber })
      : summary.checkNumber);
  }
  // The hub's per-kitchen reasons already name the kitchen ("The kitchen
  // ticket for Grill did not print: …"); naming it again reads as two.
  if (summary?.kitchenName && !message.toLowerCase().includes(summary.kitchenName.toLowerCase())) {
    context.push(translate('pos.printing.failed.kitchen', { name: summary.kitchenName }));
  }
  if (context.length === 0) return message;
  return translate('pos.printing.failed.withContext', { context: context.join(' · '), message });
}

/**
 * Which document a held alert is about: its kind, what it was printed for
 * (the check; for a KOT the order round; for an X report the session) and,
 * for a kitchen ticket, the kitchen. A repeat of the same document replaces
 * the earlier alert; a KOT that failed at two kitchens stays two alerts.
 */
export function printFailedAlertKey(
  e: { status?: unknown; checkId?: unknown; orderId?: unknown; sessionId?: unknown; kitchenId?: unknown },
  summary: PrintFailedSummary | null,
): string {
  const doc = printFailedDocument(summary?.documentType ?? e.status);
  const subject = text(e.checkId) ?? text(e.orderId) ?? text(e.sessionId) ?? '';
  return `${doc}|${subject}|${text(e.kitchenId) ?? ''}`;
}

// ── kitchen.send.failed (cloud, KC-0 / LBL-1) ───────────────────────

/** What did not come out at the kitchen. Any other status is not toasted. */
export type KitchenSendFailedReason = 'noActivePrinter' | 'noLabelTemplate' | 'labelCapExceeded';

/** The names a `kitchen.send.failed` toast leads with, each absent when unknown. */
export interface KitchenSendFailedContext {
  kitchenName: string | null;
  tableName: string | null;
  orderNo: string | null;
  checkNumber: string | null;
}

/** What the check says about the round: its table, and the round's number (or the check's, without it). */
export type KitchenSendFailedRound = Pick<KitchenSendFailedContext, 'tableName' | 'orderNo' | 'checkNumber'>;

/**
 * The event's `status`, whatever its case or separators; null for one this
 * POS has no sentence for (a later status is ignored, not guessed at).
 * Takes `unknown` because it comes off the wire.
 */
export function kitchenSendFailedReason(status: unknown): KitchenSendFailedReason | null {
  const raw = typeof status === 'string' ? status : '';
  switch (raw.toLowerCase().replace(/[^a-z]/g, '')) {
    case 'noactiveprinter': return 'noActivePrinter';
    case 'nolabeltemplate': return 'noLabelTemplate';
    case 'labelcapexceeded': return 'labelCapExceeded';
    default: return null;
  }
}

/**
 * The toast: a translated sentence for the failure, naming the kitchen when
 * its name is known, led by the table and the order round (or the check).
 */
export function kitchenSendFailedToastText(
  reason: KitchenSendFailedReason,
  ctx: KitchenSendFailedContext,
  translate: PrintFailedTranslate,
): string {
  const message = ctx.kitchenName
    ? translate(`pos.printing.kitchenSendFailed.${reason}Kitchen`, { kitchen: ctx.kitchenName })
    : translate(`pos.printing.kitchenSendFailed.${reason}`);
  // The same lead-in as a hub frame; it leaves the kitchen out when the
  // sentence already names it.
  return printFailedToastText({}, {
    documentType: null,
    reason: message,
    deviceId: null,
    kitchenName: ctx.kitchenName,
    orderNo: ctx.orderNo,
    tableName: ctx.tableName,
    checkNumber: ctx.checkNumber,
  }, translate);
}

/**
 * One alert per thing the toast names, per failure: the round only when the
 * toast names it (`ctx.orderNo`), else the check; the kitchen only when it
 * names the kitchen (`ctx.kitchenName`). So a round that missed two named
 * kitchens stays two alerts, while three kitchens a Waiter cannot name (the
 * kitchen list is refused to that role) make one: their toasts would read
 * the same. Without `ctx`, every id the event carries counts.
 */
export function kitchenSendFailedAlertKey(
  e: { status?: unknown; checkId?: unknown; orderId?: unknown; kitchenId?: unknown },
  ctx?: Pick<KitchenSendFailedContext, 'kitchenName' | 'orderNo'>,
): string {
  const round = ctx && !ctx.orderNo ? null : text(e.orderId);
  const kitchen = ctx && !ctx.kitchenName ? null : text(e.kitchenId);
  return `kitchenSend:${kitchenSendFailedReason(e.status) ?? 'unknown'}|${round ?? text(e.checkId) ?? ''}|${kitchen ?? ''}`;
}

/**
 * The table and the round's number off a check (GET pos/checks/{id} data);
 * the check's own number (CHKSL1) only when the round is not found. Null
 * when there is no check to read.
 */
export function kitchenSendFailedRound(check: unknown, orderId: string | null): KitchenSendFailedRound | null {
  if (!check || typeof check !== 'object' || Array.isArray(check)) return null;
  const c = check as Record<string, unknown>;
  const wanted = orderId?.toLowerCase();
  const orders = Array.isArray(c['orders']) ? c['orders'] : [];
  const round = wanted
    ? orders.find((o): o is Record<string, unknown> =>
        !!o && typeof o === 'object' && text((o as Record<string, unknown>)['orderId'])?.toLowerCase() === wanted)
    : undefined;
  const orderNo = label(round?.['orderNo']);
  return {
    tableName: tableLabel(c['tableName']),
    orderNo,
    checkNumber: orderNo ? null : label(c['checkSlNo']),
  };
}

/** A kitchen-list reply: the names, and whether to keep the answer for KITCHEN_NAMES_TTL_MS. */
export interface KitchenListAnswer {
  names: Map<string, string> | null;
  keep: boolean;
}

/**
 * Reads a GET stores/{id}/printing/kitchens reply (ApiService's, with
 * `httpStatus` on a refusal). A list is kept, and so is a 403 or a 404: a
 * Waiter is refused the list every time, and a hub without the route never
 * has it, so asking on every event would only be refused again. No answer,
 * a 401 (a sign-in gone bad, not a role), a 408, a 429, a 5xx, or a success
 * that is not a list is not kept, so the next event asks again.
 */
export function kitchenListAnswer(reply: unknown): KitchenListAnswer {
  const r = reply && typeof reply === 'object' ? reply as Record<string, unknown> : null;
  if (r?.['success'] === true) {
    const names = kitchenNamesFromList(r['data']);
    return { names, keep: names !== null };
  }
  const status = typeof r?.['httpStatus'] === 'number' ? r['httpStatus'] as number : null;
  return { names: null, keep: status === 403 || status === 404 };
}

/**
 * Kitchen names by id (lower case) off GET stores/{id}/printing/kitchens,
 * display name first as the chit header has it. Null when it is not a list.
 */
export function kitchenNamesFromList(list: unknown): Map<string, string> | null {
  if (!Array.isArray(list)) return null;
  const names = new Map<string, string>();
  for (const k of list) {
    if (!k || typeof k !== 'object') continue;
    const r = k as Record<string, unknown>;
    const id = text(r['id']);
    const name = text(r['displayName']) ?? text(r['name']);
    if (id && name) names.set(id.toLowerCase(), name);
  }
  return names;
}
